import type { IconName } from "./NavIcons";
import type { Gate } from "./gating";

/** A nav destination. Gate fields come from the shared {@link Gate} (ADR 274) —
 *  the same contract a home widget declares, evaluated by the same function. */
export interface NavItem extends Gate {
  to: string;
  labelKey: string;
  icon: IconName;
  badge?: "pendingDevices";
}

export interface NavSection {
  id: string;
  labelKey: string;
  items: NavItem[];
}

/**
 * The whole IA in one place: a new page is one entry here; a new vertical
 * (e.g. iC3 inventory) is one new section. Sections mirror the route split —
 * operational pages at root, setup pages under /admin.
 *
 * Gating (ADR 120): `requiresCapability` hides an item the active site can't
 * do (RTLS vs. telemetry vs. inventory); `minRole` hides setup pages from
 * under-ranked roles. `global-admin` bypasses those two gates (see navGating.ts).
 * Provisioning (ADR 175): `requiresProvisioning: "site"` hides operational nav
 * until a site exists — this one is NOT bypassed by global-admin, so a dead
 * system shows only the admin/setup section that lets you provision it.
 */
export const NAV_SECTIONS: NavSection[] = [
  {
    id: "operation",
    labelKey: "nav.group.operation",
    items: [
      // ADR 409: the sales persona's whole menu is Início, Ao Vivo and Sessões.
      { to: "/", labelKey: "nav.home", icon: "home", requiresProvisioning: "site", salesAllowed: true },
      { to: "/live", labelKey: "nav.live", icon: "live", requiresAnyCapability: ["rtls", "inventory"], requiresProvisioning: "site", salesAllowed: true },
      { to: "/fleet", labelKey: "nav.fleet", icon: "fleet", requiresCapability: "rtls", requiresProvisioning: "site" },
      // ADR 282: alerting exists (impact + inventory rules) — the row is back.
      { to: "/alerts", labelKey: "nav.alerts", icon: "alerts", requiresProvisioning: "site" },
    ],
  },
  {
    id: "analysis",
    labelKey: "nav.group.analysis",
    items: [
      { to: "/oee", labelKey: "nav.oee", icon: "oee", requiresCapability: "rtls", requiresProvisioning: "site" },
      // ADR 288: measured per-forklift movement cost (rtls: it reads sessions).
      { to: "/fleet-report", labelKey: "nav.fleetReport", icon: "fleet", requiresCapability: "rtls", requiresProvisioning: "site" },
      { to: "/safety", labelKey: "nav.safety", icon: "safety", requiresCapability: "telemetry", requiresProvisioning: "site" },
      { to: "/network", labelKey: "nav.network", icon: "network", requiresCapability: "rtls", requiresProvisioning: "site" },
    ],
  },
  {
    id: "inventory",
    labelKey: "nav.group.inventory",
    items: [
      // Capability + role gated (ADR 120), plus site-provisioned (ADR 175).
      { to: "/inventory", labelKey: "nav.inventory", icon: "oee",
        requiresCapability: "inventory", minRole: "viewer", requiresProvisioning: "site", salesAllowed: true }, // reuse an IconName; swap when a dedicated icon lands
      // ADR 365: the kits themselves — enrollment, socket, run phase, upload queue, last count.
      { to: "/inventory/kits", labelKey: "nav.inventoryKits", icon: "devices",
        requiresCapability: "inventory", minRole: "viewer", requiresProvisioning: "site" },
    ],
  },
  {
    id: "admin",
    labelKey: "nav.group.admin",
    items: [
      // Site-scoped admin pages need a site to act on (ADR 175); the true
      // bootstrap pages (tenants/sites/access) stay ungated so an empty system
      // still exposes the way to provision it.
      { to: "/admin/assets", labelKey: "nav.assets", icon: "assets", minRole: "site-admin", requiresProvisioning: "site" },
      { to: "/admin/devices", labelKey: "nav.devices", icon: "devices", badge: "pendingDevices", minRole: "site-admin", requiresProvisioning: "site" },
      { to: "/admin/warehouses", labelKey: "nav.warehouses", icon: "warehouses", minRole: "site-admin", requiresProvisioning: "site" },
      { to: "/admin/shifts", labelKey: "nav.shifts", icon: "shifts", minRole: "site-admin", requiresProvisioning: "site" },
      // ADR 288: hourly rates + cycle constants (the money the fleet report prices with).
      { to: "/admin/rates", labelKey: "nav.rates", icon: "oee", minRole: "site-admin", requiresProvisioning: "site" }, // reuse an IconName; swap when a dedicated icon lands
      // Tenants is the first step — always available to a global admin.
      { to: "/admin/tenants", labelKey: "nav.tenants", icon: "sites", minRole: "global-admin" },
      // Sites needs a tenant to exist (a site belongs to one). "tenant" is grant-derived,
      // so this ALSO admits a tenant admin without the global role (ADR 165/167/177/178).
      { to: "/admin/sites", labelKey: "nav.sites", icon: "sites", requiresProvisioning: "tenant" },
      // Access grants site access — pointless before a site exists (ADR 178).
      { to: "/admin/access", labelKey: "nav.access", icon: "access", minRole: "global-admin", requiresProvisioning: "site" },
      // The audit trail (ADR 267). Global admin, matching the gate on the actions
      // it records — and deliberately NOT provisioning-gated: a fresh tenant that
      // has done nothing still has a trail, it is simply empty, and hiding it
      // would teach an operator the product has no audit until something breaks.
      { to: "/admin/audit", labelKey: "nav.audit", icon: "access", minRole: "global-admin" },
      // The assistant's unanswered questions (ADR 412). Global admin, same rule as the
      // trail: the questions carry customer data across every tenancy. Not sales-allowed.
      { to: "/admin/assistant-gaps", labelKey: "nav.assistantGaps", icon: "access", minRole: "global-admin" },
    ],
  },
];
